import type { QuestionOption } from "../api.js";

/**
 * The answering surface for a multiple-choice question.
 *
 * Single- and multi-answer questions look different on purpose -- radio buttons
 * promise "one of these", checkboxes promise "any of these" -- and a student
 * should not have to guess which they are looking at. The number of correct
 * options is not something they may know, so the shape comes from the teacher's
 * own setting rather than from the answer key.
 */
export default function McqAnswer({
  options,
  selected,
  onChange,
  multiple,
  disabled,
  /** Marks the key and what was chosen, for review after the fact. */
  review,
}: {
  options: QuestionOption[];
  selected: string[];
  onChange: (ids: string[]) => void;
  multiple: boolean;
  disabled?: boolean;
  review?: boolean;
}) {
  function toggle(id: string) {
    if (disabled) return;
    if (multiple) {
      onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
    } else {
      onChange([id]);
    }
  }

  return (
    <div className="mcq">
      {options.map((o) => {
        const chosen = selected.includes(o.id);
        // `correct` only arrives for staff, or for a student reviewing a
        // finished answer -- so its absence is simply "not being told".
        const key = review && o.correct !== undefined ? o.correct : undefined;
        return (
          <label
            key={o.id}
            className={[
              "mcq-option",
              chosen ? "chosen" : "",
              key === true ? "right" : "",
              review && chosen && key === false ? "wrong" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <input
              type={multiple ? "checkbox" : "radio"}
              name="mcq"
              checked={chosen}
              disabled={disabled}
              onChange={() => toggle(o.id)}
            />
            <span>{o.text}</span>
            {review && key === true && <span className="badge pass">correct answer</span>}
            {review && chosen && key === false && <span className="badge fail">your answer</span>}
          </label>
        );
      })}
    </div>
  );
}
